import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Search, Download, CheckCircle2, Circle } from "lucide-react";

/**
 * Admin > Members > Payment / Account Details
 * ------------------------------------------------
 * Read-only table of every IBA / franchise's bank + KYC coordinates so the
 * accounts team can copy an account for a manual payout, spot missing IFSC
 * codes, and validate KYC completeness. Data is pulled from `/admin/members`
 * (no new backend needed — every field is already stored on the user doc).
 */
export default function AdminBankDetails() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);

  useEffect(() => {
    api.get("/admin/members").then((r) => setRows(r.data));
  }, []);

  const eligible = useMemo(
    () => rows.filter((u) => ["distributor", "franchise"].includes(u.role)),
    [rows],
  );

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return eligible.filter((u) => {
      if (onlyIncomplete && u.bank_account && u.ifsc && u.pan_number) return false;
      if (!needle) return true;
      return (
        (u.name || "").toLowerCase().includes(needle) ||
        (u.user_code || "").toLowerCase().includes(needle) ||
        (u.email || "").toLowerCase().includes(needle) ||
        (u.bank_account || "").toLowerCase().includes(needle) ||
        (u.ifsc || "").toLowerCase().includes(needle)
      );
    });
  }, [eligible, q, onlyIncomplete]);

  const complete = eligible.filter((u) => u.bank_account && u.ifsc && u.pan_number).length;

  const exportCsv = () => {
    const head = [
      "Code", "Name", "Email", "Phone", "PAN", "Account Holder",
      "Bank Name", "Account Number", "IFSC", "Branch", "KYC Status",
    ];
    const csvRows = [
      head.join(","),
      ...filtered.map((u) => [
        u.user_code, u.name, u.email, u.phone || "",
        u.pan_number || "", u.account_holder || "",
        u.bank_name || "", u.bank_account || "",
        u.ifsc || "", u.branch || "", u.kyc_status || "not_submitted",
      ].map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `samara-bank-details-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout
      title="Bank / Account Details"
      subtitle={`${filtered.length} of ${eligible.length} IBAs · ${complete} with complete bank + PAN`}
      actions={
        <button
          onClick={exportCsv}
          data-testid="bank-details-export"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 border border-navy/15 rounded-sm hover:bg-cream/40 text-navy/70"
        >
          <Download size={12} /> Export CSV
        </button>
      }
    >
      <div className="bg-white border border-border p-4 rounded-sm mb-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="bank-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, code, IFSC, account…"
            className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none"
          />
        </div>
        <button
          onClick={() => setOnlyIncomplete((v) => !v)}
          data-testid="bank-toggle-incomplete"
          className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-sm border ${
            onlyIncomplete ? "bg-rose/10 border-rose text-rose" : "bg-white border-navy/15 text-navy/70"
          }`}
        >
          {onlyIncomplete ? <CheckCircle2 size={12} /> : <Circle size={12} />}
          Only incomplete
        </button>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[1200px]" data-testid="bank-details-table">
          <thead className="bg-navy text-white">
            <tr>
              {["Code", "Name", "PAN", "Account Holder", "Bank", "Account No.", "IFSC", "Branch", "KYC"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-navy/50 text-sm">
                  {onlyIncomplete ? "Every IBA has complete bank + PAN — nothing to fix." : "No matches."}
                </td>
              </tr>
            ) : filtered.map((u) => {
              const incomplete = !u.bank_account || !u.ifsc || !u.pan_number;
              return (
                <tr key={u.user_code} data-testid={`bank-row-${u.user_code}`} className={`border-b border-border last:border-0 ${incomplete ? "bg-rose/5" : "hover:bg-cream/40"}`}>
                  <td className="px-4 py-3 font-mono text-navy font-medium">{u.user_code}</td>
                  <td className="px-4 py-3 text-navy/70">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-navy/60">{u.pan_number || <span className="text-rose/70">—</span>}</td>
                  <td className="px-4 py-3 text-navy/70 text-xs">{u.account_holder || <span className="text-navy/30">—</span>}</td>
                  <td className="px-4 py-3 text-navy/60 text-xs">{u.bank_name || <span className="text-navy/30">—</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs text-navy/70 tabular-nums">
                    {u.bank_account ? (
                      <>••••{u.bank_account.slice(-4)}<span className="ml-1 text-navy/40">({u.bank_account.length} digits)</span></>
                    ) : (
                      <span className="text-rose/70">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-navy/70">{u.ifsc || <span className="text-rose/70">—</span>}</td>
                  <td className="px-4 py-3 text-navy/60 text-xs">{u.branch || <span className="text-navy/30">—</span>}</td>
                  <td className="px-4 py-3 text-[10px] uppercase tracking-widest">
                    <span className={`px-1.5 py-0.5 rounded ${u.kyc_status === "approved" ? "bg-olive/10 text-olive" : "bg-amber-50 text-amber-700"}`}>
                      {u.kyc_status || "not_submitted"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
